import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import ScrollTop from 'yoda-site-components/lib/components/ScrollTop/ScrollTop';
import SeoSchema from 'yoda-site-components/lib/components/SeoSchema/SeoSchema';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import _get from 'lodash/get';
import {
    selectDeviceName,
    selectShouldRender,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { getCriteoData } from 'yoda-site-components/lib/actions/CriteoAction';
import {
    CMSRenderer,
    TokenProvider,
    SeoMetaTagsContentStack,
    useLivePreviewScript,
} from 'yoda-site-components/lib/cms-renderer';
import ContentstackReducer from 'yoda-site-components/lib/reducers/ContentstackReducer';
import ContentstackSaga from 'yoda-site-components/lib/sagas/ContentstackSaga';
import ContentstackActions from 'yoda-site-components/lib/cms-renderer/ContentstackActions';
import withReducerSaga from 'yoda-site-components/lib/hoc/ReducerRegistry/withReducerSaga';
import useAnalytics from 'yoda-site-components/lib/cms-renderer/hooks/useAnalytics';
import 'yoda-site-components/lib/cms-renderer/tokens/jcpenney.css';
import renderMiniPDP from '../../../helper/MiniPDPHelper';
import { onPageReady } from '../../../actions/HomePageAction';
import { HelpSection, ConnectWithUs } from '../../../globalConstants';

const buildHomeSeoSchema = (connectWithUs, helpSection, logoUrlLink) => {
    const connectWithUsLinks = (connectWithUs || []).map((i) => i.linkPath);
    const contactPhoneNumber =
        (helpSection || []).find((i) => i.id === 'give_call')?.textStrong || '';

    return [
        {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'Organization',
                    url: 'https://www.jcpenney.com/',
                    name: 'JCPenney',
                    alternateName: ['JCP', "Penney's", 'Penney OpCo LLC'],
                    logo: { '@type': 'ImageObject', url: logoUrlLink },
                    sameAs: connectWithUsLinks,
                    contactPoint: {
                        '@type': 'ContactPoint',
                        telephone: contactPhoneNumber,
                        contactType: 'Customer Support',
                    },
                },
            ],
        },
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: 'https://www.jcpenney.com/',
            potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.jcpenney.com/s?searchTerm={search_term_string}',
                'query-input': 'required name=search_term_string',
            },
        },
    ];
};

function HomePage({
    actions,
    cmsData,
    location,
    isPreview,
    clientCall,
    shouldRender,
    connectWithUs,
    helpSection,
    logoUrlLink,
    enableHomeSeoSchema,
    history,
    enableMiniPDPFeature,
    enableCriteo,
    limitInitialComponentsRendered,
    deviceName,
    contentstackLivePreview,
    livePreviewScriptUrl,
    livePreviewIframeOnly,
    pageSlug,
}) {
    const { trackPageView } = useAnalytics();

    // Contentstack Live Preview: inject the standalone script in preview mode.
    useLivePreviewScript(contentstackLivePreview, livePreviewScriptUrl, livePreviewIframeOnly);

    // SSR-safe CMS fetch: dispatched during render (not useEffect) so it runs on
    // the server's first pass. Gating mirrors IrisPage (preview = client-only fetch).
    const cmsEmpty = !cmsData || (!cmsData.pageData && !cmsData.loading && !cmsData.error);
    // Preview always renders client-side: fetch on the CLIENT only (never during
    // SSR), so the preview container is not server-rendered.
    const previewClientFetch = !__SERVER__ && isPreview && cmsEmpty;
    // Standard (non-preview) path: SSR/CSR fetch when store empty and this render
    // is allowed to trigger the call (mirrors IRIS isEmpty(irisData) && clientCall).
    const standardFetch = !isPreview && cmsEmpty && clientCall && shouldRender;
    if (previewClientFetch || standardFetch) {
        actions.getCMSPageData({ url: pageSlug });
    }

    // Client-only side effects (never needed during SSR)
    useEffect(() => {
        LocalStorage.removeData('scrollToCartridge');
        LocalStorage.removeData('galleryScrollTo');

        if (!__SERVER__ && enableCriteo) {
            actions.getCriteoData({ pageType: 'Home' });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Track page view when CMS data loads
    useEffect(() => {
        if (cmsData && cmsData.pageData) {
            const pageName =
                cmsData.pageData.meta_title || cmsData.pageData.page_title || 'JCPenney';
            actions.onPageReady(pageName, 'welcome');
            trackPageView({ pageType: 'welcome', pageName, pageUrl: '/' });
        }
    }, [cmsData]); // eslint-disable-line react-hooks/exhaustive-deps
      const seodata =cmsData && cmsData?.pageData?.seo
    const mySeoData = {
        seoMetaTitle: seodata?.meta_title,
        seoMetaDescription: seodata?.meta_description,
        openGraphTitle: seodata?.open_graph_title,
        openGraphDescription: seodata?.open_graph_description,
        openGraphImages: seodata?.open_graph_images,
        keywords: "",
        canonicalUrl: "",
        robots: ""
    };
    // === Contentstack ===
    // Gate the entire render on shouldRender, mirroring IrisPage. When
    // debug=noSSR (or any platform skip-SSR path) is active, the platform sets
    // context.shouldRender=false on the server, so we render null server-side and
    // let the client hydrate/render. Without this, CMSRenderer emits SSR markup
    // even under debug=noSSR, so the page never falls back to CSR.
    return shouldRender ? (
        <>
            <ScrollTop />
            {enableHomeSeoSchema && (
                 <SeoMetaTagsContentStack 
                seoBlock={mySeoData} 
                // preferences={sitePreferences}
                location={location}
                hostname="www.jcpenney.com"
                enableCanonicalFallback={true}
                enableIrisManualIndexing={false}
            />
            )}
            <TokenProvider brand="jcpenney">
                <CMSRenderer
                    data={cmsData && cmsData.pageData}
                    contentField="components"
                    lazyAfter={limitInitialComponentsRendered[deviceName]}
                />
            </TokenProvider>
            {enableMiniPDPFeature ? renderMiniPDP() : null}
        </>
    ) : null;
}

HomePage.propTypes = {
    actions: PropTypes.objectOf(PropTypes.func).isRequired,
    cmsData: PropTypes.object,
    location: PropTypes.object,
    isPreview: PropTypes.bool,
    clientCall: PropTypes.bool,
    shouldRender: PropTypes.bool,
    connectWithUs: PropTypes.array,
    helpSection: PropTypes.array,
    logoUrlLink: PropTypes.string,
    enableHomeSeoSchema: PropTypes.bool,
    history: PropTypes.object,
    enableMiniPDPFeature: PropTypes.bool,
    enableCriteo: PropTypes.bool,
    limitInitialComponentsRendered: PropTypes.object,
    deviceName: PropTypes.string,
    contentstackLivePreview: PropTypes.bool,
    livePreviewScriptUrl: PropTypes.string,
    livePreviewIframeOnly: PropTypes.bool,
    pageSlug: PropTypes.string,
};

HomePage.defaultProps = {
    cmsData: null,
    location: {},
    isPreview: false,
    clientCall: true,
    shouldRender: true,
    connectWithUs: [],
    helpSection: [],
    logoUrlLink: '',
    enableHomeSeoSchema: true,
    history: {},
    enableMiniPDPFeature: true,
    enableCriteo: false,
    limitInitialComponentsRendered: { mobile: 8, desktop: 9, tablet: 8 },
    deviceName: 'mobile',
    contentstackLivePreview: false,
    livePreviewScriptUrl: '',
    livePreviewIframeOnly: true,
    pageSlug: 'homepage',
};

const mapStateToProps = ({ cmsData, context }) => {
    const featureFlags = _get(context, 'featureFlags', {});
    const preferences = _get(context, 'preferences', {});

    return {
        cmsData,
        isPreview: _get(context, 'isPreview', false),
        shouldRender: selectShouldRender({ context }),
        connectWithUs: _get(preferences, 'footer.ConnectWithUs', ConnectWithUs),
        helpSection: _get(preferences, 'footer.HelpSection', HelpSection),
        logoUrlLink: _get(preferences, 'assets.linkPath', ''),
        enableHomeSeoSchema: featureFlags.enableHomeSeoSchema !== false,
        limitInitialComponentsRendered: _get(
            preferences,
            'renderingStrategy.home.limitInitialComponentsRendered',
            { mobile: 8, desktop: 9, tablet: 8 }
        ),
        deviceName: selectDeviceName({ context }),
        enableMiniPDPFeature: featureFlags.enableMiniPDPFeature !== false,
        enableCriteo: featureFlags.enableCriteo || false,
        // Page slug used for the CMS fetch; defaults to 'homepage' if not configured.
        pageSlug: _get(preferences, 'contentstack.pageSlug', 'homepage'),
        // Live preview runs only when explicitly enabled AND in preview mode.
        contentstackLivePreview:
            featureFlags.enableLivePreview === true && _get(context, 'isPreview', false),
        // CDN URL for the standalone live-preview script (configurable per env)
        livePreviewScriptUrl: _get(preferences, 'contentstack.livePreviewScriptUrl', ''),
        // Restrict live preview to the iframe by default; false = allow any preview context.
        livePreviewIframeOnly: featureFlags.enableAllowPreviewIframeOnly !== false,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({ onPageReady, getCriteoData, ...ContentstackActions }, dispatch),
});

export { HomePage };
export default withReducerSaga(
    [ContentstackReducer],
    [ContentstackSaga]
)(connect(mapStateToProps, mapDispatchToProps)(HomePage));
